/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, NgZone, OnInit} from '@angular/core';
import * as _ from 'lodash';
import template from './marketplace-product-list-page.component.html';

import {AdminListColumn, AdminResourceListPageBase} from '../../common/controller/admin-resource-list-page-base';
import {Uuid} from '../../../components/util/uuid';
import {TeamResource} from '../../../components/api/resources/resource/team.resource';
import {MarketplaceProductResource} from '../../../components/api/resources/resource/marketplace-product.resource';
import {NetworkDataResource} from '../../../components/api/resources/resource/network-data.resource';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../../components/session/local-user-permissions';

@Component({
  standalone: false,
  selector: 'adn-marketplace-product-list-page',
  template
})
export class MarketplaceProductListPageComponent extends AdminResourceListPageBase implements OnInit {
  readonly itemStateName = 'app.adm.marketplace-products.marketplace-product';

  columns: AdminListColumn[] = [
    {param: 'name'},
    {param: '', label: 'owner', sortable: false},
    {param: '', label: 'targeting', sortable: false},
    {param: 'layouts', label: 'layouts', sortable: false}
  ];

  canManageSystem = false;
  noTeams = false;
  isSetUp = false;
  editsMade = false;
  productSelectionPass = false;
  productSelectionError = false;

  productInfo: {[id: string]: any[]} = {};
  teamInfo: {[id: string]: any} = {};
  networkData: any;

  private previousNetworkData: any;
  private productQuery: Promise<void>;
  private teamQuery: Promise<void>;

  constructor(
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(MarketplaceProductResource) private MarketplaceProduct: any,
    @Inject(NetworkDataResource) private NetworkData: any,
    @Inject(TeamResource) private Team: any,
    @Inject(LocalUserPermissions) private LocalUserPermissions: any,
    private ngZone: NgZone
  ) {
    super(LocalNetworkProfile);
  }

  get teamInfoArray(): any[] {
    return _.sortBy(_.values(this.teamInfo), 'name');
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.canManageSystem = this.LocalUserPermissions.getAllNetworkPermissions().MANAGE_SYSTEM === true;

    this.productQuery = this.MarketplaceProduct.query({pageSize: 501}).$promise.then((page: any) => {
      this.ngZone.run(() => {
        this.productInfo = {};
        _.forEach(page.results, (item: any) => {
          _.forEach(item.teams, (team: any) => {
            this.productInfo[team.id] = this.productInfo[team.id] || [];
            this.productInfo[team.id].push({
              id: item.id,
              name: item.name
            });
          });
        });
      });
    });

    this.teamQuery = this.Team.query({pageSize: 501, filterBy: 'type', filterByValue: 'AX_ADVERTISER'}).$promise.then((page: any) => {
      this.ngZone.run(() => {
        this.teamInfo = {};
        _.forEach(page.results, (item: any) => {
          this.teamInfo[item.id] = {
            id: item.id,
            name: item.name
          };
        });
        this.noTeams = !page.results.length;
      });
    });

    Promise.all([this.productQuery, this.teamQuery]).then(() => {
      this.ngZone.run(() => {
        this.cleanData();
        this.NetworkData.get({filterByValue: 'defaultMarketplaceProducts', filterBy: 'type'}, (page: any) => {
          this.ngZone.run(() => {
            if (page.results.length) {
              const entry = page.results[0];
              try {
                entry.data.teamToProduct = JSON.parse(entry.data.teamToProduct);
                this.networkData = new this.NetworkData(_.cloneDeep(entry));
              } catch {
                this.networkData = null;
              }
            }

            this.cleanData();
            this.previousNetworkData = _.cloneDeep(this.networkData);
            this.isSetUp = true;
          });
        });
      });
    });
  }

  edit(): void {
    this.editsMade = true;
  }

  saveProductDefaults(): void {
    this.editsMade = false;
    this.productSelectionPass = false;
    this.productSelectionError = false;
    this.cleanData();
    this.networkData.savePartial().then(() => {
      this.ngZone.run(() => {
        this.productSelectionPass = true;
        this.previousNetworkData = _.cloneDeep(this.networkData);
      });
    }).catch(() => {
      this.ngZone.run(() => {
        this.productSelectionError = true;
      });
    });
  }

  cancelProductDefaults(): void {
    this.networkData = _.cloneDeep(this.previousNetworkData);
    this.editsMade = false;
    this.productSelectionPass = false;
    this.productSelectionError = false;
  }

  productOptionsForTeam(teamId: string): any[] {
    return this.productInfo[teamId] || [];
  }

  trackById(_: number, item: any): string {
    return item.id;
  }

  private cleanData(): void {
    this.networkData = this.networkData || new this.NetworkData({
      id: Uuid.generate(),
      name: 'Default Marketplace Products',
      type: 'defaultMarketplaceProducts'
    });
    this.networkData.data = this.networkData.data || {};
    this.networkData.data.teamToProduct = this.normalizeTeamToProduct(this.networkData.data.teamToProduct);

    _.forEach(this.teamInfo, (team: any) => {
      if (!Object.prototype.hasOwnProperty.call(this.networkData.data.teamToProduct, team.id)) {
        this.networkData.data.teamToProduct[team.id] = null;
      }
    });

    const teamsToDelete: string[] = [];
    _.forEach(this.networkData.data.teamToProduct, (_value: any, key: string) => {
      if (!this.teamInfo[key]) {
        teamsToDelete.push(key);
      }
    });

    _.forEach(teamsToDelete, (key: string) => {
      delete this.networkData.data.teamToProduct[key];
    });
  }

  private normalizeTeamToProduct(teamToProduct: any): {[id: string]: string | null} {
    if (_.isString(teamToProduct)) {
      try {
        return JSON.parse(teamToProduct);
      } catch {
        return {};
      }
    }

    return _.isPlainObject(teamToProduct) ? teamToProduct : {};
  }
}
